<script>
import Content from '@/components/dialog/content/Content';

import BodyTokenPower from '@/components/dialog/body/TokenPower';
import BodyTokenPowerSetting from '@/components/dialog/body/TokenPowerSetting';
import BodyValueInput from '@/components/dialog/body/ValueInput';

import FooterTokenPower from '@/components/dialog/footer/TokenPower';
import FooterTokenPowerSetting from '@/components/dialog/footer/TokenPowerSetting';
import FooterInformation from '@/components/dialog/footer/Information';

export default {
  name: 'Deposit',
  mixins: [Content],
  data: () => ({
    contentID: 'deposit',
    contentAction: 'common.action.deposit',
    contentActioned: 'common.actioned.deposit',
    contentActioning: 'common.actioning.deposit',
    tokenPowerStep: false,
  }),
  computed: {
    steps() {
      return [
        {
          id: 0,
          title: this.$t('trade.content.step.action', {
            action: this.$t('common.action.tokenApprove'),
          }),
          bodies: [BodyTokenPower, BodyTokenPowerSetting],
          footers: [FooterTokenPower, FooterTokenPowerSetting],
          buttons: [
            {
              title: this.$t('trade.content.button.action', {
                action: this.$t('common.action.approve'),
              }),
              action: this.enableTokenPowerMax,
            },
            {
              title: this.$t('trade.content.button.setting'),
              action: this.enableTokenPowerByInput,
            },
          ],
          disableRule: () =>
            !this.tokenPowerStep &&
            (!this.isToken || this.tokenAllowance.gt(0)),
          skipRule: () => !this.tokenPowerStep && this.tokenAllowance.gt(0),
        },
        {
          id: 1,
          title: this.$t('trade.content.step.input', {
            action: this.$t(this.contentAction),
          }),
          bodies: [BodyValueInput],
          footers: [FooterInformation],
          buttons: [
            {
              title: this.$t('trade.content.button.action', {
                action: this.$t(this.contentAction),
              }),
              action: this.deposit,
            },
          ],
        },
        {
          id: 2,
          title: this.$t('trade.content.step.complete', {
            action: this.$t(this.contentAction),
          }),
          bodies: [BodyValueInput],
          footers: [FooterInformation],
          buttons: [
            {
              title: this.$t('trade.content.button.confirm'),
              action: this.close,
            },
          ],
        },
      ];
    },
    currentBodyProps() {
      let result = this.defaultProps;

      if (this.currentBody === BodyTokenPowerSetting) {
        result = Object.assign({}, result, {
          tokenPowerValue: this.inputValue,
          tokenPowerMaxValue: this.$fromWei(this.$config.uintMaxValue, 'ether'),
        });
      } else if (this.currentBody === BodyValueInput) {
        result = Object.assign({}, result, {
          amountValue: this.inputValue,
          maxAmount: this.maxAmountString,
          inputTitle: this.$t('trade.content.placeholder.input', {
            action: this.$t(this.contentAction),
          }),
          inputPlaceholderDescription: this.$t(
            'trade.content.placeholder.inputDescription',
            { action: this.$t(this.contentAction) }
          ),
          informationTitle: this.$t('trade.content.information.walletBalance'),
          informationValue: this.informationValueString,
          disabledRules: this.disabledRules,
        });
      }

      return result;
    },
    currentFooterProps() {
      let result = this.defaultProps;

      if (this.currentFooter === FooterTokenPowerSetting) {
        result = Object.assign({}, result, {
          tokenAllowance: this.tokenAllowance,
        });
      } else if (this.currentFooter === FooterInformation) {
        result = Object.assign({}, result, {
          informationData: this.informationData,
          graphTextData: this.graphTextData,
          graphValue: this.graphValue,
        });
      }

      return result;
    },
    informationData() {
      return [
        {
          title: this.$t('trade.content.information.interestRate', {
            action: this.$t(this.contentAction),
          }),
          value: this.depositInterestRate,
        },
        {
          title: this.$t('trade.content.information.collateralRateString'),
          value: this.collateralRateString,
        },
        {
          title: this.$t('trade.content.information.tokenPrice'),
          value: this.tokenPriceString,
        },
      ];
    },
    graphTextData() {
      return [
        {
          title: this.$t('trade.content.information.borrowLimit'),
          value: this.borrowLimit,
          change: this.changeBorrowLimit,
        },
        {
          title: this.$t('trade.content.information.usingBorrowLimit'),
          value: this.borrowLimitRate,
          change: this.changeBorrowLimitRate,
        },
      ];
    },
    actionButtonDisabled() {
      return (
        this.currentStepID !== 2 &&
        (this.transactionPending ||
          !!this.emergencyMessage ||
          !this.isActionableByInput ||
          !this.$store.getters.isWalletUsable ||
          (this.currentStepID === 1 && this.isOverAllowance))
      );
    },
    disabledRules() {
      return [
        () => (this.currentStepID === 2 ? true : undefined),
        () =>
          this.informationValue.lte(0)
            ? {
                text: 'trade.content.message.nonexistent.balance',
              }
            : undefined,
      ];
    },
  },
  methods: {
    enableTokenPowerStep() {
      this.tokenPowerStep = true;
    },
    enableTokenPowerMax() {
      const from = this.$store.getters.walletAddress;
      const to = this.tokenAddress;
      const numTokens = new this.$BBN(this.$config.uintMaxValue).toHex(true);
      const tokenSendAddress = this.tokenSendAddress;

      this.addTemporaryTransaction();

      this.$approveErc20({ from, to, numTokens, tokenSendAddress })
        .then(transactionHash => {
          this.addTransaction(
            'common.action.tokenApprove',
            undefined,
            transactionHash,
            undefined,
            () => {
              this.addTemporaryTransaction();

              this.cycleSyncToken(
                () =>
                  this.cycleSyncAllowanceCheck(
                    this.$curr.getFormatedValue(
                      this.$config.uintMaxValue,
                      this.tokenID
                    )
                  ),
                () => {
                  this.enableTokenPowerStep();
                  this.nextStep();
                },
                this.cycleSyncTokenError,
                this.resolveTemporaryTransaction
              );
            }
          );
        })
        .catch(error => {
          this.addTransaction(
            'common.action.tokenApprove',
            undefined,
            undefined,
            error
          );

          this.setMessage('error', this.$getTransactionErrorMessage(error), {
            action: 'common.action.tokenApprove',
          });
        })
        .finally(() => this.resolveTemporaryTransaction());
    },
    enableTokenPowerByInput() {
      const from = this.$store.getters.walletAddress;
      const to = this.tokenAddress;
      const numTokens = this.$curr
        .getUnformatedValue(this.inputValue, this.tokenID)
        .toHex(true);
      const tokenSendAddress = this.tokenSendAddress;

      this.addTemporaryTransaction();

      this.$approveErc20({ from, to, numTokens, tokenSendAddress })
        .then(transactionHash => {
          this.addTransaction(
            'common.action.tokenApprove',
            undefined,
            transactionHash,
            undefined,
            () => {
              this.addTemporaryTransaction();

              this.cycleSyncToken(
                () => this.cycleSyncAllowanceCheck(this.inputValue),
                () => {
                  this.enableTokenPowerStep();
                  this.initializeMessage();
                  this.initializeInputValue();
                  this.nextStep();
                },
                this.cycleSyncTokenError,
                this.resolveTemporaryTransaction
              );
            }
          );
        })
        .catch(error => {
          this.addTransaction(
            'common.action.tokenApprove',
            undefined,
            undefined,
            error
          );

          this.setMessage('error', this.$getTransactionErrorMessage(error), {
            action: 'common.action.tokenApprove',
          });
        })
        .finally(() => this.resolveTemporaryTransaction());
    },
    deposit() {
      const from = this.$store.getters.walletAddress;
      const to = this.tokenSendAddress;
      const signature = this.$config.signature.deposit;
      const args = ['0x0', false];
      const sendValue = this.$curr
        .getUnformatedValue(this.inputValue)
        .toHex(true);

      let value = '0x0';

      if (this.isToken) {
        args[0] = sendValue;
      } else {
        value = sendValue;
      }

      this.addTemporaryTransaction();

      this.$sendTransaction({ from, to, signature, args, value })
        .then(transactionHash => {
          this.addTransaction(
            this.contentAction,
            this.inputValue,
            transactionHash,
            undefined,
            () => {
              this.nextStep();

              this.setMessage(
                'success',
                'common.message.transaction.successfullyAction'
              );
            }
          );
        })
        .catch(error => {
          this.addTransaction(
            this.contentAction,
            this.inputValue,
            undefined,
            error
          );

          this.setMessage('error', this.$getTransactionErrorMessage(error));

          this.nextStep();
        })
        .finally(() => this.resolveTemporaryTransaction());
    },
    messagebutton() {
      this.enableTokenPowerStep();
      this.previousStep();
      this.initializeInputValue();
      this.initializeMessage();
    },
    messageCheck() {
      if (this.currentStepID === 1) {
        if (this.isOverAllowance) {
          this.setMessage(
            'error',
            'trade.content.message.insufficient.allowance',
            undefined,
            this.tokenID === 'usdt' ? undefined : 'trade.content.message.reset',
            this.tokenID === 'usdt'
              ? 'trade.content.message.insufficient.allMustUsed'
              : undefined
          );
        } else if (
          this.message.type === 'error' &&
          this.message.text === 'trade.content.message.insufficient.allowance'
        ) {
          this.initializeMessage();
        }
      } else if (
        this.message.type === 'error' &&
        this.message.text === 'trade.content.message.insufficient.allowance'
      ) {
        this.initializeMessage();
      }
    },
  },
};
</script>
